import { RefreshCw01 } from '@untitledui/icons';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import type { OutlookResource } from '@/hooks/integrations/microsoft';
import { useOutlookStartImport } from '@/hooks/integrations/microsoft';
import { Button } from '@/lib/base/buttons/button';
import { ApiError } from '@/lib/errors';

export interface OutlookSyncNowButtonProps {
  orgId: string;
  socialAccountId: number;
}

const RESOURCES: readonly OutlookResource[] = ['mail', 'calendar'];

/**
 * Manual "Sync now" for a connected Outlook account.
 *
 * Kicks off mail + calendar delta imports via the msgraph import-jobs
 * endpoint. Graph push subscriptions normally keep the account current, but
 * they can lapse (subscriptions expire; renewal can fail) and are unavailable
 * in local dev — this button is the user-facing recovery path. A 409 from the
 * endpoint means an import is already running, which for the user's purposes
 * is success ("a sync is happening"), so it is reported as such.
 */
export default function OutlookSyncNowButton({
  orgId,
  socialAccountId,
}: OutlookSyncNowButtonProps) {
  const startImport = useOutlookStartImport(orgId);
  const [isSyncing, setIsSyncing] = useState(false);

  const handleSyncNow = useCallback(async () => {
    if (isSyncing) {
      return;
    }
    setIsSyncing(true);
    let started = 0;
    let alreadyRunning = 0;
    let failed = 0;
    for (const resource of RESOURCES) {
      try {
        await startImport.mutateAsync({ socialAccountId, resource });
        started += 1;
      } catch (err: unknown) {
        if (err instanceof ApiError && err.statusCode === 409) {
          alreadyRunning += 1;
        } else {
          failed += 1;
        }
      }
    }
    setIsSyncing(false);
    if (failed === RESOURCES.length) {
      toast.error("Couldn't start the sync. Try again.");
    } else if (started > 0) {
      toast('Sync started', {
        description: 'New emails and events will appear shortly.',
      });
    } else if (alreadyRunning > 0) {
      toast('Sync already in progress');
    }
  }, [isSyncing, startImport, socialAccountId]);

  return (
    <Button
      size='sm'
      color='secondary'
      onClick={handleSyncNow}
      isDisabled={isSyncing}
      iconLeading={RefreshCw01}
    >
      {isSyncing ? 'Syncing…' : 'Sync now'}
    </Button>
  );
}
